import { useParams, Link } from 'react-router-dom';
import { Layout } from '@/components/Layout';
import { PageHeader } from '@/components/PageHeader';
import { useContract, useUpdateVolumeDistribution } from '@/hooks/useContracts';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Pencil, Check, X, Truck } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useState } from 'react';
import { formatNumber } from '@/lib/numberFormat';
import { formatCalendarDate } from '@/lib/dateFormat';
import { contractTypeLabel } from '@/lib/contractType';
import { AuditTrail } from '@/components/contracts/AuditTrail';
import { ContractStatusBadge } from '@/components/contracts/ContractStatusBadge';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import type { VolumeDistribution } from '@/types';

function DetailRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-4 py-2.5 first:pt-0 last:pb-0">
      <dt className="shrink-0 text-sm text-muted-foreground">{label}</dt>
      <dd className="text-right text-sm font-medium">{children}</dd>
    </div>
  );
}

function DetailSkeleton() {
  return (
    <div className="space-y-6" aria-busy="true" aria-label="Loading contract">
      <div className="space-y-3">
        <Skeleton className="h-4 w-32" />
        <Skeleton className="h-9 w-80" />
        <Skeleton className="h-4 w-56" />
      </div>
      <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_20rem]">
        <Skeleton className="h-72" />
        <Skeleton className="h-72" />
      </div>
    </div>
  );
}

export default function ContractDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const { data: contract, isLoading, error } = useContract(parseInt(id || '0'));
  const updateVolumeDistribution = useUpdateVolumeDistribution();

  // State for editing confirmedBy field
  const [editingConfirmedByVdId, setEditingConfirmedByVdId] = useState<number | null>(null);
  const [confirmedByValue, setConfirmedByValue] = useState('');

  const isAdmin = user?.role === 'Admin' || false;
  const isManager = user?.role === 'BU Manager' || isAdmin || false;
  const userBusinessUnitId = user?.businessUnitId;

  // Check if user can edit confirmedBy for a given volume distribution
  const canEditConfirmedBy = (vdBusinessUnitId: number) => {
    // Admins can edit any, otherwise user must be assigned to that BU
    return isAdmin || (userBusinessUnitId && parseInt(userBusinessUnitId) === vdBusinessUnitId);
  };

  const handleEditConfirmedBy = (vd: VolumeDistribution) => {
    setEditingConfirmedByVdId(vd.id);
    setConfirmedByValue(vd.confirmedBy || '');
  };

  const handleSaveConfirmedBy = async (vdId: number) => {
    await updateVolumeDistribution.mutateAsync({
      id: vdId,
      data: { confirmedBy: confirmedByValue || undefined },
    });
    setEditingConfirmedByVdId(null);
    setConfirmedByValue('');
  };

  const handleCancelEdit = () => {
    setEditingConfirmedByVdId(null);
    setConfirmedByValue('');
  };

  if (isLoading) {
    return (
      <Layout>
        <DetailSkeleton />
      </Layout>
    );
  }

  if (error || !contract) {
    return (
      <Layout>
        <div className="text-center py-12">
          <h1 className="text-2xl font-bold">Contract not found</h1>
          <p className="mt-2 text-muted-foreground">It may have been removed, or the link is incorrect.</p>
          <Button asChild className="mt-6">
            <Link to="/contracts">Back to Contracts</Link>
          </Button>
        </div>
      </Layout>
    );
  }

  const distributions = contract.volumeDistributions ?? [];

  // Shipping instructions are stored per business unit; show every one that has text, not just the first.
  const shippingEntries = distributions.filter((vd) => vd.shippingInstructions?.trim());
  const showShippingOwner =
    shippingEntries.length > 1 && new Set(shippingEntries.map((vd) => vd.shippingInstructions)).size > 1;

  return (
    <Layout>
      <div className="space-y-6">
        <PageHeader
          back={{ to: '/contracts', label: 'All contracts' }}
          title={contract.contractName}
          badge={
            contract.isDeleted ? (
              <Badge variant="destructive">Deleted</Badge>
            ) : (
              <ContractStatusBadge startDate={contract.startDate} endDate={contract.endDate} />
            )
          }
          description={`${contract.productName} • ${contract.supplierName}`}
          actions={
            isManager &&
            !contract.isDeleted && (
              <Button asChild>
                <Link to={`/contracts/${contract.id}/edit`}>
                  <Pencil className="h-4 w-4" />
                  Edit Contract
                </Link>
              </Button>
            )
          }
        />

        {contract.isDeleted && (
          <div role="status" className="rounded-lg border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive">
            This contract has been deleted and is read-only. An admin can restore it from the contracts list.
          </div>
        )}

        <div className="grid items-start gap-6 xl:grid-cols-[minmax(0,1fr)_20rem]">
          {/* Main column: the data people come here for */}
          <div className="space-y-6">
            <Card className="overflow-hidden">
              <CardHeader className="pb-4">
                <CardTitle className="text-lg">
                  {contract.businessUnitMode === 'Several' ? 'Distribution over business units' : 'Business unit'}
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/40 hover:bg-muted/40">
                      <TableHead className="pl-6">Business Unit</TableHead>
                      <TableHead>Product</TableHead>
                      <TableHead>Supplier</TableHead>
                      <TableHead className="text-right">Volume</TableHead>
                      <TableHead className="text-right">Price / kg</TableHead>
                      <TableHead>Inco Term</TableHead>
                      <TableHead className="pr-6">Confirmed By</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {distributions.map((vd) => (
                      <TableRow key={vd.id}>
                        <TableCell className="pl-6 font-medium">{vd.businessUnitName}</TableCell>
                        <TableCell>{vd.productName}</TableCell>
                        <TableCell>{vd.supplierName}</TableCell>
                        <TableCell className="whitespace-nowrap text-right tabular-nums">
                          {formatNumber(vd.volumeKgs, 2)} <span className="text-muted-foreground">kgs</span>
                        </TableCell>
                        <TableCell className="whitespace-nowrap text-right tabular-nums">
                          {formatNumber(vd.pricePerKg, 3)} <span className="text-muted-foreground">{vd.currency}</span>
                        </TableCell>
                        <TableCell className="whitespace-nowrap">
                          {vd.incoTerm}
                          {vd.incoTermPlace && <span className="text-muted-foreground"> · {vd.incoTermPlace}</span>}
                        </TableCell>
                        <TableCell className="pr-6">
                          {editingConfirmedByVdId === vd.id ? (
                            <div className="flex items-center gap-1">
                              <Input
                                value={confirmedByValue}
                                onChange={(e) => setConfirmedByValue(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') handleSaveConfirmedBy(vd.id);
                                  if (e.key === 'Escape') handleCancelEdit();
                                }}
                                aria-label={`Confirmed by for ${vd.businessUnitName}`}
                                placeholder="Name"
                                className="h-8 w-36 text-sm"
                                autoFocus
                              />
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-8 w-8 p-0"
                                aria-label="Save"
                                onClick={() => handleSaveConfirmedBy(vd.id)}
                                disabled={updateVolumeDistribution.isPending}
                              >
                                <Check className="h-4 w-4 text-success-foreground" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-8 w-8 p-0"
                                aria-label="Cancel"
                                onClick={handleCancelEdit}
                              >
                                <X className="h-4 w-4 text-muted-foreground" />
                              </Button>
                            </div>
                          ) : (
                            <div className="flex items-center gap-1">
                              {vd.confirmedBy ? (
                                <span>{vd.confirmedBy}</span>
                              ) : (
                                <span className="text-muted-foreground">Not confirmed</span>
                              )}
                              {canEditConfirmedBy(vd.businessUnitId) && !contract.isDeleted && (
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      className="h-8 w-8 p-0 text-muted-foreground"
                                      aria-label={`Edit confirmed by for ${vd.businessUnitName}`}
                                      onClick={() => handleEditConfirmedBy(vd)}
                                    >
                                      <Pencil className="h-3.5 w-3.5" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>Edit confirmed by</TooltipContent>
                                </Tooltip>
                              )}
                            </div>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                  {distributions.length > 1 && (
                    <TableFooter>
                      <TableRow>
                        <TableCell colSpan={3} className="pl-6">Total</TableCell>
                        <TableCell className="whitespace-nowrap text-right tabular-nums">
                          {formatNumber(contract.totalVolume, 2)} <span className="font-normal text-muted-foreground">kgs</span>
                        </TableCell>
                        <TableCell colSpan={3} />
                      </TableRow>
                    </TableFooter>
                  )}
                </Table>
              </CardContent>
            </Card>

            {shippingEntries.length > 0 && (
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Truck className="h-5 w-5 text-muted-foreground" aria-hidden="true" />
                    Shipping instructions
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {(showShippingOwner ? shippingEntries : shippingEntries.slice(0, 1)).map((vd) => (
                    <div key={vd.id}>
                      {showShippingOwner && <p className="mb-1 text-sm font-medium">{vd.businessUnitName}</p>}
                      <p className="max-w-prose whitespace-pre-line text-sm">{vd.shippingInstructions}</p>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}
          </div>

          {/* Side column: reference facts and history */}
          <div className="space-y-6">
            <Card>
              <CardHeader className="pb-4">
                <CardTitle className="text-lg">Contract details</CardTitle>
              </CardHeader>
              <CardContent>
                <dl className="divide-y">
                  <DetailRow label="Total volume">
                    <span className="tabular-nums">{formatNumber(contract.totalVolume, 2)} kgs</span>
                    <span className="block text-xs font-normal text-muted-foreground tabular-nums">
                      {formatNumber(contract.totalVolume / 1000, 2)} tons
                    </span>
                  </DetailRow>
                  <DetailRow label="Type">{contractTypeLabel(contract.contractType)}</DetailRow>
                  <DetailRow label="Contract date">{formatCalendarDate(contract.contractDate)}</DetailRow>
                  <DetailRow label="Start date">{formatCalendarDate(contract.startDate)}</DetailRow>
                  <DetailRow label="End date">{formatCalendarDate(contract.endDate)}</DetailRow>
                  <DetailRow label="Assignment">
                    {contract.businessUnitMode === 'Several'
                      ? `${distributions.length} business units`
                      : 'Single business unit'}
                  </DetailRow>
                </dl>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-4">
                <CardTitle className="text-lg">Audit trail</CardTitle>
              </CardHeader>
              <CardContent>
                <AuditTrail contract={contract} />
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </Layout>
  );
}
